/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo } from 'react';
import { MultiStateV2Actions, useMultiStateV2 } from 'hooks';

import { Column } from '../Containers';
import { ScrollArea } from '../ScrollArea';
import { Skeleton } from '../UI';

import TableHeader from './Header';
import TablePagination from './Pagination';
import TableBody from './Body';
import NoDataMessage from './NoData';
import { getColumnKey, getEntryValue } from './Header/Actions/utils';

import { quickContext } from 'utils/react';
import { bestEffortSortCompare, listGet } from 'utils/array';
import { isSubstring } from 'utils/text';

import { TableProps } from './types';
import { cn } from 'styles';

type BaseContextProps = Pick<
  TableProps<any>,
  | 'columns'
  | 'headerClassName'
  | 'items'
  | 'uniqueKey'
  | 'dataClassName'
  | 'cellClassName'
  | 'rowClassName'
  | 'hidePagination'
  | 'itemsPerPage'
  | 'spacing'
  | 'rowDivisor'
>;

type ContextPropsWithDefault = Required<
  Pick<TableProps<any>, 'itemsPerPage' | 'spacing' | 'rowDivisor'>
>;

type ContextProps = BaseContextProps & ContextPropsWithDefault;

type ContextState = {
  page: number;

  searches: Record<string, string>;
  filters: Record<string, any[]>;

  sort: null | {
    key: string;
    direction: 'ASC' | 'DESC' | null;
  };
};

type TableContext = ContextProps &
  ContextState &
  Pick<MultiStateV2Actions<ContextState>, 'dispatch'>;

const { withTableContext, useTable } = quickContext(
  'table',
  ({
    columns,
    itemsPerPage = 10,
    items,
    spacing = 'small',
    rowDivisor = 'skip-header-and-last',
    ...props
  }: BaseContextProps) => {
    const [state, { dispatch }] = useMultiStateV2<ContextState>({
      page: 0,
      filters: {},
      searches: {},
      sort: null,
    });

    const columnsWithKey = useMemo(
      () =>
        columns
          .map((column, index) => ({
            ...column,
            key: getColumnKey(column, index),
          }))
          .filter((x) => !x.hide),
      [columns],
    );

    const sortItems = useCallback(
      (items: any[]) => {
        const shouldSort = state.sort?.direction;

        if (!shouldSort) return items;

        const sortColumn = listGet(columnsWithKey, state.sort!.key, 'key');

        if (!sortColumn) return items;

        return items.slice().sort((a, b) => {
          const directionMultiplier = state.sort?.direction === 'ASC' ? 1 : -1;

          const result =
            sortColumn?.sorter?.(a, b) ??
            bestEffortSortCompare(
              getEntryValue(a, sortColumn, sortColumn.sortProp),
              getEntryValue(b, sortColumn, sortColumn.sortProp),
            );

          return result * directionMultiplier;
        });
      },
      [state.sort, columnsWithKey],
    );

    const itemsToDisplay = useMemo(() => {
      const [filterEntries, searchEntries] = [state.filters, state.searches].map((mappings) =>
        Object.entries(mappings),
      );

      if (!filterEntries.length && !searchEntries.length) return sortItems(items);

      const filtered = items.filter(
        (item) =>
          filterEntries.every(([key, values]) => {
            if (!values.length) return true;

            const column = listGet(columnsWithKey, key, 'key');

            if (!column) return true;

            const refValue = getEntryValue(item, column, column.property);

            return !!values.includes(refValue);
          }) &&
          searchEntries.every(([key, search]) => {
            if (!search.trim()) return true;

            const column = listGet(columnsWithKey, key, 'key');

            if (!column) return true;

            const refValue = getEntryValue(item, column, column.property);

            return isSubstring(search.trim(), refValue);
          }),
      );

      return sortItems(filtered);
    }, [state.filters, items, state.searches, columnsWithKey, sortItems]);

    return {
      data: {
        ...state,
        dispatch,
        items: itemsToDisplay,
        columns: columns.filter((x) => !x.hide),
        itemsPerPage,
        spacing,
        rowDivisor,
        ...props,
      } as TableContext,
    };
  },
);

function TableBase<Entity>({
  hideHeader,
  hidePagination,
  loading,
  itemsPerPage = 10,
  displayNoItemMessage = true,
  items,
  className,
  tableClassName,
}: TableProps<Entity>) {
  if (loading)
    return (
      <Skeleton gap="2" width="100%" quantity={Math.min(itemsPerPage, 8)} orientation="vertical" />
    );

  if (displayNoItemMessage && !items.length) return <NoDataMessage />;

  return (
    <Column fullWidth className={className}>
      <ScrollArea orientation="horizontal">
        <table className={cn('w-full', tableClassName)}>
          {!hideHeader && <TableHeader />}

          <TableBody />
        </table>
      </ScrollArea>

      {!hidePagination && items.length > itemsPerPage && <TablePagination />}
    </Column>
  );
}

const Table = withTableContext(TableBase);

export { Table, useTable };
